// ── Theme ──────────────────────────────────────────────────────────────────
export const D = {
  bg:"#0f1117", surface:"#181c24", card:"#1e2330", border:"#2a3040",
  text:"#e8eaf0", muted:"#8892a4", accent:"#4da3f5", danger:"#f06060",
  success:"#3ecfa0", warning:"#f0a030", input:"#232939"
};

// ── Styles ─────────────────────────────────────────────────────────────────
export const st = {
  btn:(v="default")=>({padding:"7px 16px",borderRadius:7,fontSize:12,fontWeight:500,cursor:"pointer",border:"none",marginLeft:6,
    background:v==="primary"?D.accent:v==="danger"?D.danger:v==="success"?D.success:D.border,
    color:v==="primary"||v==="danger"||v==="success"?"#fff":D.text}),
  input:{background:D.input,border:`1px solid ${D.border}`,borderRadius:7,padding:"7px 12px",color:D.text,fontSize:13,width:"100%",outline:"none",boxSizing:"border-box"},
  label:{fontSize:12,color:D.muted,marginBottom:4,display:"block"},
  card:{background:D.card,border:`1px solid ${D.border}`,borderRadius:10,padding:"16px 20px",marginBottom:16},
  stat:{background:D.card,border:`1px solid ${D.border}`,borderRadius:10,padding:"16px 20px"},
  table:{width:"100%",borderCollapse:"collapse",fontSize:13},
  th:{textAlign:"left",padding:"8px 12px",color:D.muted,borderBottom:`1px solid ${D.border}`,fontWeight:500,fontSize:12},
  td:{padding:"10px 12px",borderBottom:`1px solid ${D.border}`},
  h1:{fontSize:20,fontWeight:500,margin:"0 0 20px",color:D.text},
  h2:{fontSize:16,fontWeight:500,margin:"0 0 16px",color:D.text},
  navItem:a=>({display:"flex",alignItems:"center",gap:10,padding:"9px 20px",cursor:"pointer",background:a?D.card:"transparent",color:a?D.accent:D.muted,fontWeight:a?500:400,fontSize:13,borderLeft:a?`3px solid ${D.accent}`:"3px solid transparent"}),
};

// ── Helpers ────────────────────────────────────────────────────────────────
export const toArr = v => Array.isArray(v) ? v : [];
export const fmt = n => "$"+Number(n).toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2});
export const fmtDate = d => d ? new Date(d+"T00:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}) : "—";
export const formatPhone = raw => {
  const d = String(raw||"").replace(/\D/g,"").slice(0,10);
  if (d.length<=3) return d;
  if (d.length<=6) return `${d.slice(0,3)}-${d.slice(3)}`;
  return `${d.slice(0,3)}-${d.slice(3,6)}-${d.slice(6)}`;
};
export const capitalizeWords = s => (s||'').trim().replace(/\s+/g,' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
export const calcSubtotal = items => toArr(items).reduce((s,i)=>s+(Number(i.qty)||0)*(Number(i.rate)||0),0);
// Sales tax. 0 = no tax. FL real-property contractors pay tax on materials at
// purchase and do not charge the customer (Fla. Admin. Code R. 12A-1.051).
let TAX_RATE = 0;
export const setTaxRate = pct => { TAX_RATE = (Number(pct)||0)/100; };
export const getTaxRate = () => TAX_RATE;
export const calcTax = sub => (Number(sub)||0) * TAX_RATE;
export const taxLabel = () => `Sales Tax (${+(TAX_RATE*100).toFixed(3)}%)`;
export const calcTotal = items => { const sub=calcSubtotal(items); return sub+sub*TAX_RATE; };

// ── API ────────────────────────────────────────────────────────────────────
export const API_URL = import.meta.env.VITE_API_URL || "/api";
export const getToken = () => { try { return localStorage.getItem("eb:token")||""; } catch(_) { return ""; } };

export const apiCall = async (method, path, body) => {
  const token = getToken();
  const res = await fetch(API_URL.replace("/api","")+"/api"+path, {
    method,
    headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},
    body: body ? JSON.stringify(body) : undefined,
  });
  const newToken = res.headers.get('X-New-Token');
  if (newToken) { try { localStorage.setItem('eb:token', newToken); } catch(_) {} }
  if (!res.ok) throw new Error(await apiErrorMessage(res));
  return res.json();
};
export const apiGet = path => apiCall("GET", path);
export const apiPost = (path, body) => apiCall("POST", path, body);
export const apiPut = (path, body) => apiCall("PUT", path, body);
// Pull the server's own message out of the response. The API returns
// { error: "..." } on failure; falling back to the raw text means an
// unexpected shape still says something rather than nothing.
export const apiErrorMessage = async res => {
  const raw = await res.text();
  try {
    const j = JSON.parse(raw);
    return j.error || j.message || raw || ('Request failed (' + res.status + ')');
  } catch (_) {
    return raw || ('Request failed (' + res.status + ')');
  }
};

// apiDel is only ever used for deletes, and a refused delete always deserves
// telling the user - a server that says "this invoice has a $1,000 payment
// against it" is useless if that only reaches the console. Re-throws so any
// caller that wants its own handling still gets the error.
export const apiDel = async path => {
  try {
    return await apiCall("DELETE", path);
  } catch (e) {
    try { alert(e.message || 'Could not delete that.'); } catch (_) {}
    throw e;
  }
};

// ── Bid constants ──────────────────────────────────────────────────────────
export const BID_STATUS = ["Pending","Won","Lost","Cancelled"];
export const BID_STATUS_COLOR = {Pending:"#378ADD",Won:"#1D9E75",Lost:"#E24B4A",Cancelled:"#888"};
export const BID_STATUS_BG = {Pending:"#E6F1FB",Won:"#E1F5EE",Lost:"#FCEBEB",Cancelled:"#F1EFE8"};
export const INVOICE_STATUS_COLOR = {Draft:"#888",Sent:"#378ADD",Paid:"#1D9E75",Overdue:"#E24B4A",Partial:"#9B6FD4"};
export const INVOICE_STATUS_BG = {Draft:"#F1EFE8",Sent:"#E6F1FB",Paid:"#E1F5EE",Overdue:"#FCEBEB",Partial:"#F0EAF8"};

export const LABOR_ROLES = [
  {key:"journeyman",label:"Journeyman",cost:35,bill:75},
  {key:"apprentice",label:"Apprentice",cost:22,bill:50},
  {key:"master",label:"Master Electrician",cost:60,bill:125},
];

export const LABOR_FACTORS = {
  jobType:{"New Construction":1.0,"Remodel":1.4,"Service Work":1.2},
  wireType:{"Romex (NM)":1.0,"MC Cable":1.25,"EMT Conduit":1.6,"PVC Conduit":1.5,"Mixed":1.2},
  accessType:{"Open/Easy Access":1.0,"Attic Access":1.15,"Crawl Space":1.2,"Concrete Block":1.35,"Two Story":1.2,"No Access (Full Fish)":1.5},
};

export const UNITS = ["hr","ea","job","lot","ft","sqft"];

// ── Expense categories ─────────────────────────────────────────────────────
// Shared by the Expenses page and the receipt scanner. These eight are what
// Expenses.jsx already used; the list is centralised, not changed, so no
// existing row becomes unrepresentable.
export const EXPENSE_CATEGORIES = ['Materials','Tools/Equipment','Fuel','Vehicle','Permits','Subcontractor','Office','Other','Personal'];

// The four an electrician actually photographs at a counter. Shown as one-tap
// buttons; the rest sit behind "Other". Two taps reaches anything.
export const QUICK_CATEGORIES = ['Materials','Tools/Equipment','Fuel','Permits'];

// Schedule C line per category, stored on the expense at save time so a
// historical export does not shift if this mapping is later revised.
// NOT tax advice - it categorises, the accountant decides.
export const SCHEDULE_C = {
  'Materials':       'Part II - Supplies (22) / COGS',
  'Tools/Equipment': 'Part II - Supplies (22) or Depreciation (13)',
  'Fuel':            'Part II - Car and truck expenses (9)',
  'Vehicle':         'Part II - Car and truck expenses (9)',
  'Permits':         'Part II - Taxes and licenses (23)',
  'Subcontractor':   'Part II - Contract labor (11)',
  'Office':          'Part II - Office expense (18)',
  'Other':           'Part V - Other expenses (27a)',
  // Deliberately an explicit sentence, never null. A null schedule_c risks
  // being swept into "uncategorised" at year end, which is wrong in the
  // dangerous direction; this way the accountant SEES the decision.
  'Personal':        'NOT DEDUCTIBLE - personal',
};

// A line the business paid for but cannot deduct - a snack on a hardware run.
// It is still STORED: the receipt's expense rows ARE its line items (there is
// no separate receipt_lines table), so dropping it would make a correct
// receipt fail reconcile() forever. Recorded, reconciled, never deducted.
// ONE definition, because every deductible total must agree with every other.
export const NON_DEDUCTIBLE_CATEGORY = 'Personal';
export const isDeductible = e => String((e && e.category) || '') !== NON_DEDUCTIBLE_CATEGORY;

// Fallback wording when a company has not set its own. Kept in code rather than
// as a column default so it can be improved without a migration and without
// freezing existing users to today's text. DocView, the email body and the
// Settings placeholder all read these, so the document can never disagree with
// itself the way the printed and emailed terms used to.
export const DEFAULT_PAYMENT_TERMS = "Invoices are due within 30 days of the invoice date. A late fee of 1.5% per month (18% annually) may be applied to any balance unpaid after the due date.";
// Used when the company has late fees switched ON. Saying "will" to a company
// that has them OFF would be a promise the app does not keep - late_fee_enabled
// defaults to 0, so most companies get the "may" wording above.
export const DEFAULT_PAYMENT_TERMS_ENFORCED = "Invoices are due within 30 days of the invoice date. A late fee of 1.5% per month (18% annually) will be applied to any balance unpaid after the due date.";
export const defaultPaymentTerms = ci => (ci && ci.lateFeeEnabled) ? DEFAULT_PAYMENT_TERMS_ENFORCED : DEFAULT_PAYMENT_TERMS;
export const DEFAULT_SCOPE_NOTE = "This price covers electrical work only. Drywall repair, painting, and other finish patchwork resulting from the work performed are not included and remain the homeowner's responsibility.";

// The 39 placeholder materials that shipped here from January 2026 are gone.
// They carried INVENTED prices - 12/2 Romex at $0.55 against CED's real
// $0.63594 - and because the lookup is by exact name, "12/2 Romex" matched the
// placeholder while the contractor's own supply-house price sat under the
// supplier's name and never got used. A made-up default is worse than none.
//
// Replaced by the real CED material list: names, units and package sizes, and
// NO prices. MAT_ALIASES maps trade language onto it, so "12/2 romex" finds
// "NM12/2WG 250' COILS".
export { DEFAULT_MAT_CATALOG, MAT_ALIASES } from './matCatalog.js';
